/**
 * Sample tracking IDs: PrescriptionId-patientId-labTestId
 * Used for labels even when the sample_transactions API is not deployed yet.
 */

const toPositiveId = (value) => {
  if (value == null || value === "") return null;
  if (typeof value === "object" && !Array.isArray(value)) {
    return toPositiveId(value.id ?? value.prescriptionId ?? value.patientId ?? value.labTestId);
  }
  const n = Number(value);
  if (!Number.isFinite(n) || n <= 0) return null;
  return Math.trunc(n);
};

export const toYesNo = (value) => {
  if (value === true || value === 1) return "YES";
  if (value === false || value === 0) return "NO";
  const raw = String(value ?? "").trim().toUpperCase();
  if (raw === "YES" || raw === "Y" || raw === "TRUE") return "YES";
  if (raw === "NO" || raw === "N" || raw === "FALSE") return "NO";
  return value ? "YES" : "NO";
};

/** True when value is an explicit YES/NO / boolean flag (not a lab result string). */
const isExplicitYesNoFlag = (value) => {
  if (value === true || value === false || value === 1 || value === 0) return true;
  if (value == null || value === "") return false;
  const raw = String(value).trim().toUpperCase();
  return raw === "YES" || raw === "NO" || raw === "Y" || raw === "N" || raw === "TRUE" || raw === "FALSE";
};

/**
 * Resolve sample-transaction result YES/NO flag.
 * Prefer dedicated flag fields so lab-result text in `result` is never treated as the flag.
 */
export const resolveSampleResultFlag = (record = {}, overrides = {}) => {
  const candidates = [
    overrides.resultFlag,
    overrides.hasResult,
    overrides.resultEntered,
    overrides.result,
    record.resultFlag,
    record.hasResult,
    record.resultEntered,
    // Only use `result` when it is clearly a YES/NO / boolean flag.
    isExplicitYesNoFlag(record.result) ? record.result : undefined,
  ];
  for (const candidate of candidates) {
    if (candidate === undefined) continue;
    return toYesNo(candidate);
  }
  return "NO";
};

export const getSamplePrescriptionId = (record = {}) =>
  toPositiveId(
    record.prescriptionId ??
      record.prescription_id ??
      record.ipdPrescriptionId ??
      record.ipd_prescription_id ??
      record.opdPrescriptionId ??
      record.opd_prescription_id ??
      record.prescription?.id
  );

export const getSamplePatientId = (record = {}) =>
  toPositiveId(record.patientId ?? record.patient_id ?? record.patient?.id);

export const getSampleLabTestId = (record = {}) =>
  toPositiveId(
    record.labTestId ??
      record.lab_test_id ??
      record.testId ??
      record.test_id ??
      record.catalogLabTestId
  );

export const getSampleReportId = (record = {}) =>
  toPositiveId(record.reportId ?? record.report_id ?? record.pathLabReportId);

export const getSampleInvoiceId = (record = {}) =>
  toPositiveId(record.invoiceId ?? record.invoice_id ?? record.invoice?.id);

export const getSampleAdmissionId = (record = {}) =>
  toPositiveId(record.admissionId ?? record.admission_id ?? record.admission?.id);

/**
 * Unique sample collected id.
 * Missing prescription/admission uses 0 so the format stays stable.
 */
export const buildSampleCollectedId = (record = {}) => {
  const explicit =
    record.uniqueSampleCollectedId ??
    record.unique_sample_collected_id ??
    record.sampleCollectedId ??
    record.sample_collected_id;
  if (explicit != null && String(explicit).trim() !== "") {
    return String(explicit).trim();
  }

  const prescriptionId =
    getSamplePrescriptionId(record) ?? getSampleAdmissionId(record) ?? 0;
  const patientId = getSamplePatientId(record) ?? 0;
  const labTestId = getSampleLabTestId(record) ?? 0;
  return `${prescriptionId}-${patientId}-${labTestId}`;
};

export const canBuildSampleCollectedId = (record = {}) => {
  const patientId = getSamplePatientId(record);
  const labTestId = getSampleLabTestId(record);
  return patientId != null && labTestId != null;
};

/** Upsert allowed when we have a stable unique id, report id, or patient + lab test. */
export const canUpsertSampleTransaction = (record = {}) => {
  const explicit =
    record.uniqueSampleCollectedId ??
    record.unique_sample_collected_id ??
    record.sampleCollectedId ??
    record.sample_collected_id;
  if (explicit != null && String(explicit).trim() !== "") return true;
  if (getSampleReportId(record)) return true;
  return canBuildSampleCollectedId(record);
};

export const shouldDisplaySampleCollectedId = (status) => {
  const rs = String(status || "").trim().toUpperCase();
  return rs === "COLLECTED" || rs === "COMPLETE" || rs === "COMPLETED";
};

export const getDisplaySampleCollectedId = (record = {}, status = null) => {
  const rs =
    status ??
    record.resultStatus ??
    record.testStatus ??
    record.sampleStatus ??
    record.sampleCollectionStatus;
  if (!shouldDisplaySampleCollectedId(rs) && !record.sampleCollected && !record.sample_collected) {
    return "";
  }
  if (!canBuildSampleCollectedId(record) && !record.uniqueSampleCollectedId) {
    return "";
  }
  return buildSampleCollectedId(record);
};

export const buildSampleTransactionPayload = (record = {}, overrides = {}) => {
  const merged = { ...record, ...overrides };
  const prescriptionId = getSamplePrescriptionId(merged);
  const patientId = getSamplePatientId(merged);
  const labTestId = getSampleLabTestId(merged);
  const explicitUniqueId =
    merged.uniqueSampleCollectedId ??
    merged.unique_sample_collected_id ??
    merged.sampleCollectedId ??
    merged.sample_collected_id;
  const uniqueSampleCollectedId = explicitUniqueId
    ? String(explicitUniqueId).trim()
    : canBuildSampleCollectedId(merged)
      ? buildSampleCollectedId(merged)
      : null;
  const billed = overrides.billed ?? merged.billed ?? Boolean(getSampleInvoiceId(merged));
  const sampleCollected =
    overrides.sampleCollected ?? merged.sampleCollected ?? merged.sample_collected ?? true;
  // IMPORTANT: do not use lab-result text from `merged.result` as the YES/NO flag.
  const resultYesNo = resolveSampleResultFlag(merged, overrides);
  const reportId = getSampleReportId(merged);

  return {
    ...(uniqueSampleCollectedId
      ? {
          uniqueSampleCollectedId,
          unique_sample_collected_id: uniqueSampleCollectedId,
        }
      : {}),
    prescriptionId,
    prescription_id: prescriptionId,
    patientId,
    patient_id: patientId,
    labTestId,
    lab_test_id: labTestId,
    billed: toYesNo(billed),
    bill: toYesNo(billed),
    invoiceId: getSampleInvoiceId(merged),
    invoice_id: getSampleInvoiceId(merged),
    sampleCollected: toYesNo(sampleCollected),
    sample_collected: toYesNo(sampleCollected),
    result: resultYesNo,
    resultEntered: resultYesNo,
    hasResult: resultYesNo,
    resultFlag: resultYesNo,
    reportId,
    report_id: reportId,
    admissionId: getSampleAdmissionId(merged),
    admission_id: getSampleAdmissionId(merged),
    patientType: merged.patientType || merged.patient_type || null,
    patientName: merged.patientName || merged.patient_name || null,
    labTestName:
      merged.labTestName ||
      merged.lab_test_name ||
      merged.testName ||
      merged.test_name ||
      merged.resolvedTestName ||
      null,
  };
};
